import { usePaymentResult } from '../hooks/payments'
import { useCallback, useContext, useMemo, type JSX } from 'react'
import { PaymentContext } from '../contexts/payment'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Container from '../components/container'
import { useQuery } from '@tanstack/react-query'
import { PaymentIntentQuery } from '../queries/payments'
import PaymentDetails from '../components/payment-details'
import Button from '../components/button'
import { useBrandBasePath } from '../hooks/brand'

function convertPaymentStatusToStripeStatus (paymentStatus: string): string {
  switch (paymentStatus) {
    case 'Processing':
      return 'processing'
    case 'Completed':
      return 'succeeded'
    default:
      return 'requires_payment_method'
  }
}

export default function PaymentCompleted (): JSX.Element {
  const { payment } = useContext(PaymentContext)
  const { t } = useTranslation()
  const { status, paymentIntent, clientSecret } = usePaymentResult()
  const [searchParams] = useSearchParams()
  const { data } = useQuery(PaymentIntentQuery(payment.market, paymentIntent, clientSecret))
  const navigate = useNavigate()
  const basePath = useBrandBasePath()

  const isInteracPayment = useMemo(() => searchParams.get('redirect_status') === 'succeeded' && searchParams.get('method') === 'interac', [searchParams])
  const paymentStatus = useMemo(() => data?.status ?? status ?? convertPaymentStatusToStripeStatus(payment.status), [data?.status, payment.status, status])
  const isDeposit = payment.type.toLowerCase() === 'deposit'

  const handleRetry = useCallback(() => {
    navigate(`${basePath}/payments/${payment.id}`)
  }, [basePath, navigate, payment.id])

  return (
    <Container>
      <div className='pb-8'>
        <h2
          className='text-brand-primary text-lg font-bold my-8'
        >{t('payment:payment_completed_title', 'Thank you for your payment')}
        </h2>
        <p
          className='text-sm my-3'
        >{isInteracPayment
            ? t('payment:payment_completed_description_interac', "Your payment is on its way. We'll send you a confirmation email once your Interac e-Transfer has been received.")
            : isDeposit
              ? t('payment:payment_completed_description_deposit', 'Your deposit payment has been received.')
              : t('payment:payment_completed_description_full', 'Your full payment has been received.')}
        </p>
        <PaymentDetails payment={payment} paymentIntent={data ?? undefined}>
          {paymentStatus === 'processing' && (
            <>
              <dd className='hidden' />
              <dt className='col-span-2 text-left font-normal'>
                {t('payment:processing_message', 'The payment is currently in ‘Processing’ status. It may take 4-5 business days until the it reconciles with your booking. You will then receive a payment confirmation and your booking confirmation.')}
              </dt>
            </>
          )}
          {!isInteracPayment && paymentStatus !== 'succeeded' && paymentStatus !== 'processing' && (
            <>
              <dd className='hidden' />
              <dt className='col-span-2 text-right'>
                <Button onClick={handleRetry}>{t('payment:retry', 'Retry')}</Button>
              </dt>
            </>
          )}
        </PaymentDetails>
      </div>
    </Container>
  )
}
